import React, {useCallback, useMemo, useState} from 'react';
import {TeamSession, PlayerSession} from '../../interfaces';
import {Alert, Box, Button, Grid, Modal, Typography} from '@mui/material';
import styles from '../../styles';
import FormTextField from './FormTextField';
import {Control, FieldValues, useForm} from 'react-hook-form';
import FormRadioGroup from './FormRadioGroup';
import {updateSessionMetaData} from '../../api/teamSessionAPI';
import {useTranslation} from 'react-i18next';
import {getTeamSessionDetailAsync} from '../../store/teamSessionSlice';
import {useAppDispatch} from '../../store/hooks';
import {LocationType} from '../../enums';
import {getPlayerSessionDetailAsync} from '../../store/playerSessionSlice';

type SessionMetaDataEditForm = {
  locationType: LocationType | null;
  teamScore: number | null;
  opponentName: string | null;
  opponentScore: number | null;
};

interface SessionMetaDataProps {
  teamSession?: TeamSession;
  playerSession?: PlayerSession;
}

const returnScoreAsNullOrNumber = (score: number | string | null) => {
  if (score === null || score === undefined) {
    return null;
  }
  if (typeof score === 'string') {
    return parseInt(score);
  }
  return score;
};

const SessionMetaDataEdit: React.FC<SessionMetaDataProps> = ({
  teamSession,
  playerSession,
}) => {
  const {t} = useTranslation();
  const dispatch = useAppDispatch();
  const [showUpdateDialog, setShowUpdateDialog] = useState(false);
  const [pending, setPending] = useState(false);
  const sessionDefaultAndMin = useMemo(() => {
    if (teamSession) {
      if (!teamSession.teamSessionGroup) {
        return {};
      }
      return {
        locationType: teamSession.teamSessionGroup?.teamSessions?.find(
          ts => ts.sessionDetail?.locationType,
        )?.sessionDetail?.locationType,
        teamScore: teamSession.teamSessionGroup?.teamSessions?.reduce(
          (acc, ts) => {
            if (
              ts.sessionDetail?.teamScore &&
              ts.sessionDetail?.teamScore > acc
            ) {
              return ts.sessionDetail?.teamScore;
            }
            return acc;
          },
          0,
        ),
        opponentName: teamSession.teamSessionGroup?.teamSessions?.find(
          ts => ts.sessionDetail?.opponentName,
        )?.sessionDetail?.opponentName,
        opponentScore: teamSession.teamSessionGroup?.teamSessions?.reduce(
          (acc, ts) => {
            if (
              ts.sessionDetail?.opponentScore &&
              ts.sessionDetail?.opponentScore > acc
            ) {
              return ts.sessionDetail?.opponentScore;
            }
            return acc;
          },
          0,
        ),
      };
    }
    // TODO should I add defaults for playersession?
    return {};
  }, [teamSession]);
  const {control, handleSubmit, watch} = useForm<SessionMetaDataEditForm>({
    defaultValues: {
      locationType:
        teamSession?.sessionDetail?.locationType ??
        playerSession?.sessionDetail?.locationType ??
        sessionDefaultAndMin?.locationType ??
        null,
      teamScore:
        teamSession?.sessionDetail?.teamScore ??
        playerSession?.sessionDetail?.teamScore ??
        sessionDefaultAndMin?.teamScore ??
        null,
      opponentName:
        teamSession?.sessionDetail?.opponentName ??
        playerSession?.sessionDetail?.opponentName ??
        sessionDefaultAndMin?.opponentName ??
        null,
      opponentScore:
        teamSession?.sessionDetail?.opponentScore ??
        playerSession?.sessionDetail?.opponentScore ??
        sessionDefaultAndMin?.opponentScore ??
        null,
    },
  });

  const teamScoreError = useMemo(() => {
    const scoreInput = watch('teamScore');
    if (!sessionDefaultAndMin.teamScore || !scoreInput) {
      return false;
    }
    return scoreInput < sessionDefaultAndMin.teamScore;
  }, [watch, sessionDefaultAndMin.teamScore]);

  const opponentScoreError = useMemo(() => {
    const scoreInput = watch('opponentScore');
    if (!sessionDefaultAndMin.opponentScore || !scoreInput) {
      return false;
    }
    return scoreInput < sessionDefaultAndMin.opponentScore;
  }, [watch, sessionDefaultAndMin.opponentScore]);

  const handleUpdateSubmit = useCallback(
    (data: SessionMetaDataEditForm) => {
      setPending(true);
      if (teamSession?.id) {
        updateSessionMetaData({
          teamSessionId: teamSession.id,
          locationType: data.locationType,
          teamScore: returnScoreAsNullOrNumber(data.teamScore),
          opponentName: data.opponentName,
          opponentScore: returnScoreAsNullOrNumber(data.opponentScore),
        })
          .then(() => {
            setPending(false);
            dispatch(getTeamSessionDetailAsync(teamSession.id!));
            setShowUpdateDialog(false);
          })
          .catch((e: Error) => {
            setPending(false);
            console.error(e);
          });
      } else if (playerSession?.id) {
        updateSessionMetaData({
          playerSessionId: playerSession.id,
          locationType: data.locationType,
          teamScore: returnScoreAsNullOrNumber(data.teamScore),
          opponentName: data.opponentName,
          opponentScore: returnScoreAsNullOrNumber(data.opponentScore),
        })
          .then(() => {
            setPending(false);
            dispatch(getPlayerSessionDetailAsync(playerSession.id!));
            setShowUpdateDialog(false);
          })
          .catch((e: Error) => {
            setPending(false);
            console.error(e);
          });
      }
    },
    [teamSession, playerSession, dispatch],
  );

  return (
    <>
      <Button onClick={() => setShowUpdateDialog(true)}>
        {t('general.edit')}
      </Button>
      <Modal
        open={showUpdateDialog}
        onClose={() => setShowUpdateDialog(false)}
        aria-labelledby="modal-modal-title"
        aria-describedby="modal-modal-description">
        <Box sx={styles.modal}>
          <Typography variant="h4" sx={{mb: 2}}>
            {t('general.updateGameDetails')}
          </Typography>
          <form onSubmit={handleSubmit(handleUpdateSubmit)} className="form">
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box sx={{mb: 2}}>
                  <FormRadioGroup
                    control={control as unknown as Control<FieldValues>}
                    name="locationType"
                    options={[
                      {value: LocationType.home, label: t('general.home')},
                      {value: LocationType.away, label: t('general.away')},
                    ]}
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <FormTextField
                  control={control as unknown as Control<FieldValues>}
                  name="opponentName"
                  label={t('general.opponentName') as string}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <FormTextField
                  control={control as unknown as Control<FieldValues>}
                  name="teamScore"
                  label={t('general.teamScore') as string}
                  numbersOnly
                />
                {teamScoreError && (
                  <Alert severity="warning" sx={{mb: 2}}>
                    {t('general.updateGameDetailsWarning')}
                  </Alert>
                )}
              </Grid>
              <Grid item xs={12} md={6}>
                <FormTextField
                  control={control as unknown as Control<FieldValues>}
                  name="opponentScore"
                  label={t('general.opponentScore') as string}
                  numbersOnly
                />
                {opponentScoreError && (
                  <Alert severity="warning" sx={{mb: 2}}>
                    {t('general.updateGameDetailsWarning')}
                  </Alert>
                )}
              </Grid>
            </Grid>
            <Button variant="contained" disabled={pending} type="submit">
              {t('general.submit')}
            </Button>
            <Button
              sx={{ml: 2}}
              disabled={pending}
              onClick={() => setShowUpdateDialog(false)}>
              {t('general.cancel')}
            </Button>
          </form>
        </Box>
      </Modal>
    </>
  );
};

export default SessionMetaDataEdit;
